/**
 * useCytoscapePathFinding Hook
 *
 * Binds the shared shortest-path highlight (see ./path-highlight) to a cy
 * instance and exposes it as the `visualizePath` / `clearPath` pair published
 * on CytoscapeGraphRef. `visualizePath` is driven by the AI assistant's
 * Dijkstra result; `clearPath` also backs the Escape-to-dismiss handler. All
 * class work lives in ./path-highlight so the styling has one owner.
 */

import type { Core } from 'cytoscape'
import { useCallback } from 'react'
import { applyPathHighlight, clearPathHighlight } from './path-highlight'
import type { PathResult } from './types'

export interface UseCytoscapePathFindingOptions {
  cy: Core | null
}

export interface UseCytoscapePathFindingReturn {
  visualizePath: (pathResult: PathResult) => void
  clearPath: () => void
}

export function useCytoscapePathFinding({
  cy,
}: UseCytoscapePathFindingOptions): UseCytoscapePathFindingReturn {
  const visualizePath = useCallback(
    (pathResult: PathResult) => {
      if (!cy) return
      applyPathHighlight(cy, pathResult.nodeIds ?? [], pathResult.edgeIds ?? [])
    },
    [cy]
  )

  const clearPath = useCallback(() => {
    if (cy) clearPathHighlight(cy)
  }, [cy])

  return { visualizePath, clearPath }
}
